import React, { useState } from 'react';
import { 
  Modal, 
  Button, 
  Form, 
  Row, 
  Col, 
  Alert
} from 'react-bootstrap';
import { 
  FaPlus, 
  FaTimes
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';

const FieldDependencyForm = ({ 
  show, 
  onHide, 
  fields, 
  objectName, 
  objectId,
  existingDependencies = [],
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    controlling_field: '',
    dependent_field: '',
    is_active: true
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Filter picklist fields from the passed fields
  const picklistFields = fields && fields.length > 0 ? fields.filter(field => 
    field.data_type === 'picklist' || field.data_type === 'multipicklist'
  ) : [];

  // Filter out controlling field and already dependent fields from dependent field options
  const getDependentFieldOptions = () => {
    if (!formData.controlling_field) return picklistFields;
    const alreadyDependentFields = getAlreadyDependentFields();
    return picklistFields.filter(field => 
      field.id !== formData.controlling_field && 
      !alreadyDependentFields.includes(field.id)
    );
  };

  // Check if a field is already a dependent field in existing dependencies
  const isFieldAlreadyDependent = (fieldId) => {
    return existingDependencies.some(dep => dep.dependent_field === fieldId);
  };

  // Get fields that are already dependent (should be excluded from dependent field options)
  const getAlreadyDependentFields = () => {
    return existingDependencies.map(dep => dep.dependent_field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.controlling_field || !formData.dependent_field) {
      setError('Both controlling and dependent fields are required');
      return;
    }

    // Check if the dependent field is already dependent on another field
    if (isFieldAlreadyDependent(formData.dependent_field)) {
      const dependentFieldName = picklistFields.find(f => f.id === formData.dependent_field)?.field_label || 'Selected field';
      setError(`${dependentFieldName} is already a dependent field. Each field can only depend on one controlling field.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Debug logging
      console.log('FieldDependencyForm - objectId received:', objectId);
      console.log('FieldDependencyForm - objectId type:', typeof objectId);
      console.log('FieldDependencyForm - objectId length:', objectId ? objectId.length : 'undefined');

      const dependencyData = {
        controlling_field: formData.controlling_field,
        dependent_field: formData.dependent_field,
        is_active: formData.is_active,
        object_config_id: objectId, // Use the actual object config ID
        object_name: objectName // Also send object name as fallback
      };

      console.log('FieldDependencyForm - dependencyData to send:', dependencyData);

      const dataApi = new DataApi('picklist/field-dependencies');
      const response = await dataApi.create(dependencyData);
      console.log('FieldDependencyForm - response received:', response);
      if (response.data && response.data.success) {
        // Reset form
        setFormData({
          controlling_field: '',
          dependent_field: '',
          is_active: true
        });
        setError('');
        
        // Show success toast
        setToastMessage('Field dependency created successfully!');
        setToastVariant('success');
        setShowToast(true);
        
        // Close modal and notify parent after a short delay
        setTimeout(() => {
          onSuccess(); // Notify parent component
          onHide(); // Close modal
        }, 1000);
      } else {
        // Handle server error responses (success: false)
        const errorMessage = response.data?.message || response.message;
        console.log('FieldDependencyForm - server error message:', errorMessage);
        
        // Display the exact server message
        setError(errorMessage || 'Failed to save dependency');
      }
    } catch (error) {
      console.error('Error saving dependency:', error);
      
      // Extract error message from server response
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Failed to save dependency');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      controlling_field: '',
      dependent_field: '',
      is_active: true
    });
    setError('');
    onHide();
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  const handleControllingFieldChange = (e) => {
    setFormData({
      ...formData, 
      controlling_field: e.target.value, 
      dependent_field: '' // Reset dependent field when controlling field changes
    });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Field Dependency</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {picklistFields.length === 0 ? (
          <Alert variant="warning">
            <strong>No picklist fields available!</strong>
            <br />
            This object doesn't have any fields with data type 'picklist' or 'multipicklist'.
            <br />
            You need to create picklist fields first before you can set up dependencies.
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Controlling Field *</Form.Label>
                  <Form.Select
                    value={formData.controlling_field}
                    onChange={handleControllingFieldChange}
                    required
                  >
                    <option value="">-- Select Controlling Field --</option>
                    {picklistFields.map(field => (
                      <option key={field.id} value={field.id}>
                        {field.field_label} ({field.field_name}) - {field.data_type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    This field will control the dependent field's available values
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dependent Field *</Form.Label>
                  <Form.Select
                    value={formData.dependent_field}
                    onChange={(e) => setFormData({...formData, dependent_field: e.target.value})}
                    required
                    disabled={!formData.controlling_field}
                  >
                    <option value="">-- Select Dependent Field --</option>
                    {getDependentFieldOptions().map(field => (
                      <option key={field.id} value={field.id}>
                        {field.field_label} ({field.field_name}) - {field.data_type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    This field's values will depend on the controlling field selection.
                    <br />
                    <strong>Note:</strong> Each field can only depend on one controlling field.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={handleClose}>
                <FaTimes className="me-1" />
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                <FaPlus className="me-1" />
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
      
      {/* Success Toast */}
      <AppToast
        show={showToast}
        onClose={handleToastClose}
        message={toastMessage}
        variant={toastVariant}
      />
    </Modal>
  );
};

export default FieldDependencyForm;
